import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { useAuth } from "../../../features/auth/context/AuthContext";
import { useTranslation } from "../../../i18n";

export function MerchantAccountControls({ compact = false }: { compact?: boolean }) {
  const { theme, toggleTheme } = useTheme();
  const { userEmail, logout, isAdmin } = useAuth();
  const { t, locale, setLocale } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const userInitial = userEmail?.trim()?.charAt(0)?.toUpperCase() || "U";

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const toggleLanguage = () => setLocale(locale === "de" ? "en" : "de");
  const themeLabel =
    theme === "dark"
      ? t("layout.authHeader.switchToLightAria")
      : t("layout.authHeader.switchToDarkAria");

  return (
    <div className={`merchant-account${compact ? " merchant-account--compact" : ""}`} ref={menuRef}>
      {!compact && (
        <button
          type="button"
          className="merchant-icon-key merchant-account__language"
          onClick={toggleLanguage}
          aria-label={
            locale === "de"
              ? t("layout.authHeader.switchToEnglishAria")
              : t("layout.authHeader.switchToGermanAria")
          }
        >
          {locale.toUpperCase()}
        </button>
      )}
      <button
        type="button"
        className="merchant-icon-key"
        onClick={toggleTheme}
        aria-label={themeLabel}
        title={themeLabel}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          {theme === "dark" ? "dark_mode" : "light_mode"}
        </span>
      </button>
      <button
        type="button"
        className="merchant-account__user"
        onClick={() => setOpen((value) => !value)}
        aria-label={t("layout.authHeader.userMenuAria")}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {userInitial}
      </button>

      {open && (
        <div className="merchant-menu merchant-account__menu" role="menu">
          <div className="merchant-account__identity">
            <span>{userEmail}</span>
            <span className="merchant-account__role">
              {isAdmin ? t("sidebar.adminBadge") : t("layout.merchantShell.account")}
            </span>
          </div>
          {compact && (
            <button type="button" role="menuitem" onClick={toggleLanguage}>
              <span className="material-symbols-outlined" aria-hidden="true">
                language
              </span>
              {locale === "de"
                ? t("layout.authHeader.switchToEnglishAria")
                : t("layout.authHeader.switchToGermanAria")}
            </button>
          )}
          {isAdmin && (
            <Link to="/admin" role="menuitem" onClick={() => setOpen(false)}>
              <span className="material-symbols-outlined" aria-hidden="true">
                admin_panel_settings
              </span>
              {t("header.userMenu.adminPortal")}
            </Link>
          )}
          <Link to="/settings" role="menuitem" onClick={() => setOpen(false)}>
            <span className="material-symbols-outlined" aria-hidden="true">
              settings
            </span>
            {t("header.userMenu.settings")}
          </Link>
          <Link to="/" role="menuitem" onClick={() => setOpen(false)}>
            <span className="material-symbols-outlined" aria-hidden="true">
              north_west
            </span>
            {t("layout.merchantShell.backToWebsite")}
          </Link>
          <button
            type="button"
            role="menuitem"
            className="merchant-menu__danger"
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              logout
            </span>
            {t("header.userMenu.logout")}
          </button>
        </div>
      )}
    </div>
  );
}

